import { useEffect, useState } from "react";
import { toast } from "sonner";

import ConfirmMessageModal from "components/ConfirmMessageModal";
import FilePreviewModal, { detectPreviewKind } from "components/FilePreviewModal";
import type { FilePreviewKind } from "components/FilePreviewModal";
import radioCheck from "assets/svg/radio-check.svg";
import { formatTmNoForDisplay } from "utils/tmNumberDisplay";

import {
  OppositionWorkflowStep,
  getOppositionCounterByOpponent,
  getOppositionProgress,
  sendOppositionNoticeToClientForCounter,
  type CounterByOpponentItem,
  type OppositionProgressStep,
} from "api/opposition";

export const OPPOSITION_NOTICE_TO_CLIENT_FOR_COUNTER_FORM_ID =
  "opposition-notice-to-client-for-counter-form";

function RadioCircle({ active }: { active: boolean }) {
  if (active) return <img src={radioCheck} alt="" width={14} height={14} />;
  return (
    <span
      aria-hidden
      className="inline-block w-[14px] h-[14px] rounded-full border border-[#C3C3C3] bg-white"
    />
  );
}

type Props = {
  leadId?: string | null;
  onSuccess?: (leadId: string) => void;
  onSentStatusChange?: (sent: boolean) => void;
};

export default function OppositionNoticeToClientForCounterView({
  leadId,
  onSuccess,
  onSentStatusChange,
}: Props) {
  const [loading, setLoading] = useState<boolean>(!!leadId);
  const [marks, setMarks] = useState<CounterByOpponentItem[]>([]);
  const [sending, setSending] = useState(false);
  const [hasSent, setHasSent] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmMode, setConfirmMode] = useState<"send" | "resend">("send");

  const [preview, setPreview] = useState<{
    url: string;
    name: string;
    kind: FilePreviewKind;
  } | null>(null);

  useEffect(() => {
    onSentStatusChange?.(hasSent);
  }, [hasSent, onSentStatusChange]);

  useEffect(() => {
    if (!leadId) {
      setMarks([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    Promise.all([
      getOppositionCounterByOpponent(leadId),
      getOppositionProgress(leadId).catch(() => null),
    ])
      .then(([data, progress]) => {
        if (cancelled) return;
        setMarks(data.conflictingMarks);
        if (progress) {
          const step = progress.steps.find(
            (s: OppositionProgressStep) =>
              s.slug === OppositionWorkflowStep.OP_S3_02_NoticeToClientForCounter
          );
          setHasSent(Boolean(step?.isCompleted));
        }
      })
      .catch((err) => {
        if (!cancelled) {
          toast.error(
            err instanceof Error ? err.message : "Failed to load Counter Statement notice"
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId]);

  const eligibleMarks = marks.filter(
    (m) => m.counterFiled === true && m.counterStatementFileKey
  );
  const eligibleMarkIds = eligibleMarks.map((m) => m.id);

  const canSend = eligibleMarks.length > 0;

  const triggerSend = (mode: "send" | "resend") => {
    if (!leadId || sending) return;
    if (!canSend) {
      toast.error(
        "Upload the Counter Statement PDF in the previous step before sending the notice."
      );
      return;
    }
    setConfirmMode(mode);
    setShowConfirm(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId || sending) return;
    if (hasSent) {
      onSuccess?.(leadId);
      return;
    }
    triggerSend("send");
  };

  const handleConfirmProceed = () => {
    if (!leadId) return;
    setShowConfirm(false);
    setSending(true);
    (async () => {
      try {
        await sendOppositionNoticeToClientForCounter(leadId, {
          conflictingMarkIds: eligibleMarkIds,
        });
        setHasSent(true);
        toast.success(
          confirmMode === "resend"
            ? "Counter Statement notice resent to client successfully."
            : "Counter Statement notice sent to client successfully."
        );
        onSuccess?.(leadId);
      } catch (err) {
        toast.error(err instanceof Error ? err.message : "Failed to send notice");
      } finally {
        setSending(false);
      }
    })();
  };

  if (!leadId) {
    return (
      <div className="flex flex-col gap-[22px] w-full max-w-[1085px] py-12">
        <p className="text-sm text-[#6D6D6D]">
          Please complete the previous steps to access this step.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex flex-col gap-[22px] w-full max-w-[1085px] py-12">
        <p className="text-sm text-[#6D6D6D]">Loading…</p>
      </div>
    );
  }

  return (
    <form
      id={OPPOSITION_NOTICE_TO_CLIENT_FOR_COUNTER_FORM_ID}
      onSubmit={handleSubmit}
      className="flex flex-col gap-[22px] w-full max-w-[1085px]"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      {/* TM tabs (only show eligible marks) */}
      {eligibleMarks.length > 1 && (
        <div className="flex flex-row flex-wrap gap-3">
          {eligibleMarks.map((m) => (
            <div
              key={m.id}
              className="flex items-center gap-2 px-2.5 py-2.5 rounded-[6px] h-10 min-w-[140px] bg-[#222222] text-white"
              style={{ fontSize: "0.75rem" }}
            >
              <RadioCircle active />
              {formatTmNoForDisplay(m.applicationNo ?? "")}
            </div>
          ))}
        </div>
      )}

      <div className="flex flex-col gap-5 self-stretch bg-white border border-[#EEEEEE] rounded-[10px] p-5">
        <div className="flex flex-col gap-1">
          <p
            className="font-semibold text-[#101111]"
            style={{ fontSize: "13px", lineHeight: "100%", letterSpacing: "0.01em" }}
          >
            Counter Statement Documents
          </p>
          <p
            className="font-normal text-[#7D7D7D]"
            style={{ fontSize: "11px", lineHeight: "20px", letterSpacing: "0.04em" }}
          >
            Review the uploaded Counter Statement PDFs that will be forwarded to the client.
          </p>
        </div>

        {eligibleMarks.length === 0 ? (
          <p className="text-xs text-[#6D6D6D]">
            No Counter Statement PDFs are available. Upload the PDF in the previous step
            first.
          </p>
        ) : (
          <div className="flex flex-col gap-2 self-stretch">
            {eligibleMarks.map((m) => (
              <div
                key={m.id}
                className="flex items-center justify-between gap-3 px-3 py-2 rounded-[8px] border border-[#EEE] bg-[#FCFCFC]"
              >
                <div className="flex flex-col min-w-0 flex-1">
                  <span className="text-xs text-[#222222] truncate">
                    {m.counterStatementFilename ?? "Counter_Statement.pdf"}
                  </span>
                  <span className="text-[11px] text-[#6D6D6D] truncate">
                    {formatTmNoForDisplay(m.applicationNo ?? "")}
                  </span>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {m.counterStatementDownloadUrl && (
                    <>
                      <button
                        type="button"
                        onClick={() =>
                          setPreview({
                            url: m.counterStatementDownloadUrl!,
                            name: m.counterStatementFilename ?? "Counter_Statement.pdf",
                            kind: detectPreviewKind(
                              m.counterStatementFilename ?? "Counter_Statement.pdf"
                            ),
                          })
                        }
                        className="px-3 h-8 rounded-[8px] border border-[#D5D3E0] bg-white text-xs font-medium text-[#2E3037] hover:bg-gray-50"
                      >
                        Preview
                      </button>
                      <a
                        href={m.counterStatementDownloadUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        download={m.counterStatementFilename ?? "Counter_Statement.pdf"}
                        className="px-3 h-8 inline-flex items-center justify-center rounded-[8px] border border-[#D5D3E0] bg-white text-xs font-medium text-[#2E3037] hover:bg-gray-50"
                      >
                        Download
                      </a>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {hasSent && canSend && (
          <button
            type="button"
            disabled={sending}
            onClick={() => triggerSend("resend")}
            className="self-start inline-flex items-center justify-center h-9 px-4 text-[12px] font-medium leading-5 text-[#2E3037] bg-white border border-[#D5D3E0] rounded-[10px] whitespace-nowrap hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {sending ? "Sending…" : "Resend Message"}
          </button>
        )}
      </div>

      <FilePreviewModal
        open={!!preview}
        onClose={() => setPreview(null)}
        name={preview?.name ?? ""}
        url={preview?.url}
        kind={preview?.kind}
      />

      <ConfirmMessageModal
        open={showConfirm}
        onClose={() => setShowConfirm(false)}
        previewContent={`Counter Statement PDF will be forwarded to the client for ${
          eligibleMarks.length
        } application${eligibleMarks.length === 1 ? "" : "s"}.`}
        onConfirm={handleConfirmProceed}
        disableConfirm={!canSend}
      />
    </form>
  );
}
